// emoji.js
// Named exports: EmojiPicker, parseEmojis, insertEmojiAtCursor, EMOJI_MAP, SKIN_TONES

import React, { useEffect, useMemo, useRef, useState } from "react";

/* ============================================================
   SKIN TONES
============================================================ */
export const SKIN_TONES = [
  { id: "default", label: "Default", mod: "" },
  { id: "light", label: "🏻", mod: "🏻" },
  { id: "medium-light", label: "🏼", mod: "🏼" },
  { id: "medium", label: "🏽", mod: "🏽" },
  { id: "medium-dark", label: "🏾", mod: "🏾" },
  { id: "dark", label: "🏿", mod: "🏿" }
];

/* ============================================================
   RAW EMOJI LIST — EXACTLY AS USER PROVIDED (NO KEYWORDS)
============================================================ */

const RAW_EMOJIS = [
"😄","😁","😆","🥹","😅","😂","🤣","🥲","☺","😊","😇","🙂","🙃","😉","😌",
"😍","🥰","😘","😗","😙","😚","😋","😛","😝","😜","🤪","🤨","🧐","🤓","😎",
"🥸","🤩","🥳","🙂‍↕","😏","😒","🙂‍↔","😞","😔","😟","😕","🙁","☹","😣","😖",
"😫","😩","🥺","😢","😭","😤","😠","😡","🤬","🤯","😳","🥵","🥶","😶‍🌫","😱",
"😨","😰","😥","😓","🤗","🤔","🫣","🤭","🫢","🫡","🤫","🫠","🤥","😶","🫥","😐",
"🫤","😑","🫨","😬","😯","😯","😦","😧","😮","😲","🥱","🫩","😴","🤤","😪",
"😮‍💨","😵","😵‍💫","🤐","🥴","🤢","🤮","🤧","😷","🤒","🤕","🤑","🤠","😈",
"👿","👹","👺","🤡","💩","👻","💀","☠","👽","👾","🤖","🎃","😺","😸","😹",
"😻","😼","😽","🙀","😿","😾","🫶","🤲🏻","🤛","🤜","🫷","🫸","🫸","🤞","✌",
"🫰🏻","🤟","🤘","👌","🤌","🤏","🫳","🫴","👈","👉","👉","👆","👇","☝","✋",
"🤚","🖐","🖖","👋","🤙","🫲","🫲","🫱","💪","🦾","🖕","✍","🙏","🫵","🦶",
"🦵","🦿","💄","💋","👄","🫦","🦷","👅","👂","🦻","👃","🫆","👣","👁","👀",
"🫀","🫁","🧠","🗣","👤","👥","🫂","👶","👧","🧒","👦","👩","🧑","👨","👩‍🦱",
"🧑‍🦱","👨‍🦱","👩‍🦰","🧑‍🦰","👨‍🦰","👨‍🦰","👱‍♀","👳","👳🏻‍♂","🧕","👮‍♀",
"👮","👮‍♂","👷‍♀","👷","👷‍♂","💂‍♀","💂","💂‍♂","🕵‍♀","🕵","🕵‍♀","🕵‍♀","🕵"
];

/* ============================================================
   BUILD EMOJI CATEGORIES (NO KEYWORDS)
============================================================ */

function chunk(arr, size) {
  const out = [];
  for (let i = 0; i < arr.length; i += size) out.push(arr.slice(i, i + size));
  return out;
}

// We group the huge list into 6 sections automatically
const CATEGORY_SIZE = Math.ceil(RAW_EMOJIS.length / 6);

const EMOJI_CATEGORIES = {
  "Smileys": chunk(RAW_EMOJIS, CATEGORY_SIZE)[0],
  "Faces": chunk(RAW_EMOJIS, CATEGORY_SIZE)[1],
  "Reactions": chunk(RAW_EMOJIS, CATEGORY_SIZE)[2],
  "Gestures": chunk(RAW_EMOJIS, CATEGORY_SIZE)[3],
  "People": chunk(RAW_EMOJIS, CATEGORY_SIZE)[4],
  "Misc": chunk(RAW_EMOJIS, CATEGORY_SIZE)[5],
};

/* ============================================================
   BUILD LOOKUP MAP
============================================================ */

export const EMOJI_MAP = {};
export const EMOJI_INDEX = [];

(function buildIndex() {
  Object.entries(EMOJI_CATEGORIES).forEach(([category, list]) => {
    list.forEach((char) => {
      const short = `:${char.codePointAt(0).toString(16)}:`; // auto shortcode
      const entry = {
        short,
        char,
        category,
        skinSupport: /[\u270a-\u270d\u1f44a-\u1f590]/.test(char)
      };
      EMOJI_MAP[short] = char;
      EMOJI_INDEX.push(entry);
    });
  });
})();

/* ============================================================
   PARSE EMOJIS
============================================================ */
export function parseEmojis(text = "") {
  if (!text) return text;
  let out = text;
  Object.keys(EMOJI_MAP).forEach((short) => {
    out = out.split(short).join(EMOJI_MAP[short]);
  });
  return out;
}

/* ============================================================
   INSERT EMOJI INTO INPUT
============================================================ */
export function insertEmojiAtCursor(inputEl, emoji) {
  if (!inputEl || !emoji) return;
  try {
    const start = inputEl.selectionStart;
    const end = inputEl.selectionEnd;
    const val = inputEl.value;
    const newVal = val.slice(0, start) + emoji + val.slice(end);
    inputEl.value = newVal;
    const pos = start + emoji.length;
    inputEl.setSelectionRange(pos, pos);
    inputEl.dispatchEvent(new Event("input", { bubbles: true }));
    return;
  } catch (e) {}

  // Fallback for contenteditable
  const sel = window.getSelection();
  if (!sel || !sel.rangeCount) return;
  const range = sel.getRangeAt(0);
  range.deleteContents();
  range.insertNode(document.createTextNode(emoji));
}

/* ============================================================
   EMOJI PICKER COMPONENT
============================================================ */
// UPDATED EmojiPicker — NO SEARCH, NO INTERNAL TOGGLE BUTTON, NO ABSOLUTE STYLES

/* ============================================================
   EMOJI PICKER COMPONENT (WITH RECENT)
============================================================ */
export function EmojiPicker({ onSelect, recentEmojis = [] }) {
  const baseCategories = Object.keys(EMOJI_CATEGORIES);

  const categories = useMemo(() => {
    return recentEmojis.length
      ? ["Recent", ...baseCategories]
      : baseCategories;
  }, [recentEmojis]);

  const [activeCat, setActiveCat] = useState(categories[0]);

  useEffect(() => {
    if (recentEmojis.length && activeCat !== "Recent") {
      setActiveCat("Recent");
    }
  }, [recentEmojis]);

  function pick(char) {
    onSelect(char);
  }

  const list =
    activeCat === "Recent"
      ? recentEmojis
      : EMOJI_CATEGORIES[activeCat] || [];

   return (
    <div
      style={{
        width: "100%",
        height: "min(320px, 60vh)",
        background: "#fff",
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
      }}
    >
      {/* CATEGORY TABS */}
      <div
        style={{
          display: "flex",
          overflowX: "auto",
          borderBottom: "1px solid #eee",
          flexShrink: 0,
        }}
      >
        {categories.map((cat) => (
          <button
            key={cat}
            onClick={() => setActiveCat(cat)}
            style={{
              padding: "6px 10px",
              background: activeCat === cat ? "#f0f0f0" : "transparent",
              border: "none",
              cursor: "pointer",
              whiteSpace: "nowrap",
              fontWeight: activeCat === cat ? 600 : 400,
            }}
          >
            {cat === "Recent" ? "🕘 Recent" : cat}
          </button>
        ))}
      </div>

      {/* EMOJI GRID */}
      <div
        style={{
          flex: 1,
          overflowY: "auto",
          padding: 8,
          display: "grid",
          gridTemplateColumns: "repeat(auto-fill, minmax(36px, 1fr))",
          gap: 4,
        }}
      >
        {list.map((char, i) => (
          <button
            key={`${char}-${i}`}
            onClick={() => pick(char)}
                      style={{
              fontSize: 22,
              padding: 8,
              minWidth: 40,
              minHeight: 40,
              background: "none",
              border: "none",
              cursor: "pointer",
              borderRadius: 6,
            }}
          >
            {char}
          </button>
        ))}

        {activeCat === "Recent" && list.length === 0 && (
          <div
            style={{
              gridColumn: "1 / -1",
              textAlign: "center",
              color: "#777",
              padding: 12,
            }}
          >
            No recent emojis
          </div>
        )}
      </div>
    </div>
  );
}

export default EmojiPicker;
